export type ProductWorkflow = { input: string; output: string; measure: string };
const workflow = (input: string, output: string, measure: string): ProductWorkflow => ({ input, output, measure });

// One launch brief per product, in the same order as the page's product collection.
export const productWorkflows: Record<string, ProductWorkflow[]> = {
  insurance: [
    workflow(
      'Protection need, asset type and occupancy.',
      'Show relevant preparation questions, then continue to the insurer’s quote or advisor service.',
      'Completed finders and quote-service clicks.',
    ),
    workflow(
      'Product interest and which required information is ready.',
      'Create a preparation checklist. Collect sensitive documents only through the insurer’s designated secure service.',
      'Checklist completion and continuation to the quote flow.',
    ),
    workflow(
      'Changes in circumstances, assets or contact preferences.',
      'Summarize topics for a policy review and direct the customer to their agent.',
      'Review requests and completed agent handoffs.',
    ),
    workflow(
      'Claim category and the type of help requested.',
      'Show the insurer’s document checklist and official claims channel, without deciding whether a claim is covered.',
      'Checklist use and clicks to the claims service.',
    ),
    workflow(
      'Responses to an insurer-reviewed risk scenario.',
      'Explain the answer and link to the relevant educational resource. Do not produce an underwriting score.',
      'Scenario completion and topics that need clearer explanations.',
    ),
    workflow(
      'Agent role, product topic and responses to practice questions.',
      'Show launch guidance and a recap of topics to revisit before an advisor conversation.',
      'Training completion and responses by topic.',
    ),
  ],
  banking: [
    workflow(
      'Account purpose, service channels and everyday usage.',
      'Explain relevant account categories and continue to the bank’s comparison or application page.',
      'Finder completion and application-page clicks.',
    ),
    workflow(
      'Buying stage, property status and preparation needs.',
      'Show a document and question checklist before an advisor booking. Keep affordability checks with the bank.',
      'Preparation completion and booking clicks.',
    ),
    workflow(
      'A savings goal and time horizon, without account credentials.',
      'Build an educational action checklist and link to the bank’s current savings information. Do not project returns.',
      'Completed plans and savings-resource clicks.',
    ),
    workflow(
      'Self-reported habits and the topics a customer wants to understand.',
      'Offer bank-reviewed learning resources and support contacts, without making a credit assessment.',
      'Completed checks and resource selections.',
    ),
    workflow(
      'Business stage, payment channels and cash flow needs.',
      'Create a conversation brief for the bank’s small business team.',
      'Completed briefs and sales-contact requests.',
    ),
    workflow(
      'Product topic and answers to short comprehension questions.',
      'Explain terms with examples and link to current product disclosures.',
      'Lesson completion and questions answered incorrectly.',
    ),
  ],
  payments: [
    workflow(
      'Spending priorities, preferred benefits and service needs.',
      'Explain relevant card categories with links to current issuer terms. Applications remain with the issuer.',
      'Completed comparisons and issuer application clicks.',
    ),
    workflow(
      'An upcoming occasion and benefit interests.',
      'Show the relevant benefit category, terms and any issuer-managed activation steps.',
      'Benefit discovery and clicks to terms or activation.',
    ),
    workflow(
      'Which setup task the cardholder wants to complete.',
      'Guide the visitor to official activation, wallet or card-control instructions. Never request card credentials.',
      'Guide completion and exits to issuer services.',
    ),
    workflow(
      'Business size, sales channels and settlement needs.',
      'Summarize requirements for the merchant sales team or continue to current payment products.',
      'Qualified merchant inquiries and contact requests.',
    ),
    workflow(
      'Reward interests and the topic a cardholder wants explained.',
      'Use issuer-approved earning examples and link to the official redemption service.',
      'Education completion and redemption-service clicks.',
    ),
    workflow(
      'Choices in an issuer-reviewed suspicious-message scenario.',
      'Explain the warning signs and link to the issuer’s reporting channel.',
      'Scenario completion and learning gaps by question.',
    ),
  ],
  healthcare: [
    workflow(
      'A service task and preferred resource, not symptoms.',
      'Continue to a directory, booking portal or support channel owned by the health organization.',
      'Resource discovery and destination clicks.',
    ),
    workflow(
      'A benefit topic and whether the member needs information or support.',
      'Show plan-reviewed explanations and an official contact route. Eligibility remains with the plan.',
      'Topic completion and support-channel selections.',
    ),
    workflow(
      'The appointment information category the visitor needs.',
      'Show organization-approved checklists and link to the appointment portal for individual instructions.',
      'Checklist use and appointment-resource clicks.',
    ),
    workflow(
      'A learning topic and responses to reviewed educational questions.',
      'Present relevant information and the organization’s own care resources, without diagnostic recommendations.',
      'Education completion and selected learning topics.',
    ),
    workflow(
      'The member’s chosen setup task, such as finding digital services.',
      'Introduce the portal, directory and support contacts in a short sequence.',
      'Onboarding completion and resource selections.',
    ),
    workflow(
      'A service rating and a predefined feedback category.',
      'Ask a relevant follow-up and offer an official support channel. Avoid requesting clinical details in free text.',
      'Response completion and service themes, subject to the agreed privacy rules.',
    ),
  ],
  'real-estate': [
    workflow(
      'Location, property type, space priorities and budget range.',
      'Map preferences to current project or listing pages and explain the filters used.',
      'Completed finders and project-page clicks.',
    ),
    workflow(
      'Moving timeline, buying stage and viewing preference.',
      'Create a sales brief and route it to the team responsible for the selected project.',
      'Completed briefs, viewing requests and CRM-reported attendance.',
    ),
    workflow(
      'Selected floor plans, amenities and neighborhood topics.',
      'Let visitors explore a project story and request the current brochure or a viewing.',
      'Plan interactions, brochure clicks and viewing requests.',
    ),
    workflow(
      'Budget topics and financing questions the buyer wants to prepare.',
      'Produce a discussion checklist and link to an advisor. Do not estimate lending eligibility.',
      'Completed checklists and advisor-contact clicks.',
    ),
    workflow(
      'Property type, location, selling timeline and service needs.',
      'Prepare an intake summary for the valuation team, without generating a valuation.',
      'Completed seller briefs and appraisal requests.',
    ),
    workflow(
      'Project interests and the information an investor wants to review.',
      'Present approved project materials and disclosures, then route an information request to the team.',
      'Document interest and qualified follow-up requests.',
    ),
  ],
  retail: [
    workflow(
      'Use case, practical constraints and product preferences.',
      'Explain a shortlist and link to current catalog items or collection filters.',
      'Finder completion, product clicks and configured commerce events.',
    ),
    workflow(
      'The features a shopper wants to compare and their intended use.',
      'Explain tradeoffs between relevant product types before sending the shopper to product pages.',
      'Comparison completion and product-page continuation.',
    ),
    workflow(
      'Recipient, occasion, interests and budget range.',
      'Show a gift shortlist from the current catalog with a reason for each selection.',
      'Completed finders and gift-product clicks.',
    ),
    workflow(
      'Routine, existing products and compatibility requirements.',
      'Explain a compatible set based on merchant-maintained rules and link to the corresponding products.',
      'Bundle interest and configured add-to-cart events.',
    ),
    workflow(
      'Selected product features and answers to discovery questions.',
      'Adapt the launch story and direct interested shoppers to the current product page.',
      'Story completion and product interest by feature.',
    ),
    workflow(
      'Product model and the setup or care task selected.',
      'Show the brand’s instructions, related resources and support route.',
      'Guide completion and support-link selections.',
    ),
  ],
  telecommunications: [
    workflow(
      'Individual, household or business needs and usage priorities.',
      'Explain relevant plan categories and link to the provider’s current offers and coverage checks.',
      'Completed finders and plan-page clicks.',
    ),
    workflow(
      'Device use, preferred features and budget range.',
      'Explain a device shortlist based on the provider’s current catalog, then link to product pages.',
      'Completed comparisons and device-page clicks.',
    ),
    workflow(
      'Travel topic and the destination information the visitor wants to review.',
      'Explain where to check current roaming terms and direct the customer to the provider’s travel service.',
      'Guide completion and clicks to current roaming information.',
    ),
    workflow(
      'The service and setup task the customer needs help with.',
      'Show provider-approved setup instructions and the official account or activation destination.',
      'Guide completion and activation-service clicks.',
    ),
    workflow(
      'Service type and the support task selected.',
      'Direct the customer to the provider’s troubleshooting or support flow, without requesting account credentials.',
      'Successful resource discovery and support-channel selections.',
    ),
    workflow(
      'Business size, locations and connectivity requirements.',
      'Create a structured requirements brief for the provider’s business team.',
      'Completed briefs and sales-contact requests.',
    ),
  ],
};
